import { PoSelectOption } from '@po-ui/ng-components';

import { CurationCategory, CurationKind } from './interfaces/curation.interface';
import { FINDING_RESOLUTIONS } from '../../review/review-feedback.constants';

/**
 * Categorias para `kind = finding` (labels pt-BR).
 * Ordem segue a árvore de decisão do Guia de Classificação: o acerto (`correct`)
 * primeiro, depois os motivos do erro na sequência em que o guia os apresenta.
 */
export const FINDING_CATEGORIES: PoSelectOption[] = [
  { value: 'correct', label: 'Achado correto' },
  { value: 'hallucinated_symbol', label: 'Símbolo/função não existe' },
  { value: 'language_specific', label: 'Específico da linguagem' },
  { value: 'false_alarm_logic', label: 'Lógica já garante' },
  { value: 'business_rule_intent', label: 'Regra de negócio intencional' },
  { value: 'missing_context', label: 'Contexto faltando' },
  { value: 'against_convention', label: 'Padrão correto do Protheus' },
  { value: 'wrong_severity', label: 'Severidade errada' },
  { value: 'wrong_line', label: 'Linha errada' },
  { value: 'low_value', label: 'Pouco relevante' }
];

/**
 * Categorias para `kind = review` (labels pt-BR).
 * Ordem segue a árvore de decisão: review sólido (`good`) primeiro, depois os motivos.
 */
export const REVIEW_CATEGORIES: PoSelectOption[] = [
  { value: 'good', label: 'Review sólido' },
  { value: 'missed_issue', label: 'Problema real não detectado' },
  { value: 'too_noisy', label: 'Excesso de achados' },
  { value: 'wrong_status', label: 'Status/score errado' },
  { value: 'inconsistent', label: 'Resultado inconsistente' }
];

/** Retorna as opções de categoria conforme o `kind` do item. */
export function categoriesByKind(kind: CurationKind): PoSelectOption[] {
  return kind === 'review' ? REVIEW_CATEGORIES : FINDING_CATEGORIES;
}

/** Label pt-BR de uma categoria (qualquer kind); vazio quando sem categoria. */
export function categoryLabel(value?: string): string {
  if (!value) { return ''; }
  const option = [...FINDING_CATEGORIES, ...REVIEW_CATEGORIES].find(o => o.value === value);
  return (option?.label as string) || value;
}

/**
 * "Quando usar" de cada categoria — resumo de uma linha do Guia de Classificação.
 * Exibido como ajuda no modal de curadoria ao selecionar a categoria.
 */
export const CATEGORY_HINT: Record<CurationCategory, string> = {
  // finding
  hallucinated_symbol: 'O modelo citou função/parâmetro/método que não existe no Protheus/AdvPL/TLPP.',
  language_specific: 'O símbolo existe, mas o modelo errou o comportamento específico da linguagem.',
  false_alarm_logic: 'O próprio código já garante a condição — o modelo não acompanhou o fluxo.',
  business_rule_intent: 'Tecnicamente questionável, mas intencional por regra de negócio do cliente.',
  missing_context: 'O modelo acertaria se tivesse visto outro arquivo / o fonte chamador.',
  against_convention: 'É o padrão correto do Protheus/equipe — o modelo não conhece a convenção.',
  wrong_severity: 'Achado válido, mas o modelo classificou com severidade errada.',
  wrong_line: 'Achado válido, mas o modelo apontou a linha errada.',
  low_value: 'O achado procede, mas tem impacto baixo demais para justificar ação.',
  correct: 'O modelo acertou — verdadeiro positivo de qualidade para o golden set.',
  // review
  missed_issue: 'O review não detectou um problema real que existia no código.',
  too_noisy: 'O review gerou achados demais / muito ruído, dificultando a triagem.',
  wrong_status: 'O status ou score do summary está errado.',
  inconsistent: 'Reruns do mesmo código geram resultados diferentes entre si.',
  good: 'O review como um todo está sólido — serve como exemplo positivo.'
};

/** "Quando usar" da categoria; vazio quando sem categoria. */
export function categoryHint(value?: string): string {
  if (!value) { return ''; }
  return CATEGORY_HINT[value as CurationCategory] || '';
}

/**
 * Mapa categoria → lever (alavanca de correção) derivado. Read-only na UI.
 * Categorias sem alavanca (`correct`, `good`) mapeiam para string vazia.
 */
export const LEVER_BY_CATEGORY: Record<CurationCategory, string> = {
  // finding
  false_alarm_logic: 'system_prompt',
  hallucinated_symbol: 'dictionary_tool',
  business_rule_intent: 'not_fixable',
  against_convention: 'system_prompt',
  missing_context: 'context_assembly',
  language_specific: 'language_block',
  wrong_severity: 'output_format',
  wrong_line: 'system_prompt',
  low_value: 'output_format',
  correct: '',
  // review
  missed_issue: 'instructions',
  too_noisy: 'output_format',
  wrong_status: 'system_prompt',
  inconsistent: 'system_prompt',
  good: ''
};

/** Descrição de um lever: rótulo amigável, o que significa e onde acionar. */
export interface LeverInfo {
  label: string;
  meaning: string;
  action: string;
}

/**
 * O que significa cada lever (alavanca) e onde a melhoria deve acontecer.
 * O lever é derivado da categoria; esta tabela é apenas explicativa na UI.
 */
export const LEVER_INFO: Record<string, LeverInfo> = {
  system_prompt: {
    label: 'Prompt do sistema (system prompt)',
    meaning: 'O modelo precisa de instrução melhor sobre lógica/convenção/formato.',
    action: 'ajustar o system prompt'
  },
  language_block: {
    label: 'Regras da linguagem (language block)',
    meaning: 'Falta documentação sobre comportamento específico do AdvPL/TLPP.',
    action: 'adicionar regras da linguagem no contexto enviado ao DTA'
  },
  dictionary_tool: {
    label: 'Dicionário de funções (dictionary tool)',
    meaning: 'O modelo não conhece funções/símbolos do Protheus.',
    action: 'alimentar a dictionary tool no DTA'
  },
  context_assembly: {
    label: 'Montagem de contexto (context assembly)',
    meaning: 'O modelo precisaria ver mais arquivos/contexto.',
    action: 'melhorar a montagem de contexto enviada ao LLM'
  },
  output_format: {
    label: 'Formato de saída (output format)',
    meaning: 'O modelo precisa ajustar como formata/prioriza os achados.',
    action: 'ajustar o formato de saída e a priorização'
  },
  instructions: {
    label: 'Instruções de review (instructions)',
    meaning: 'O modelo precisa de instrução mais clara sobre o que revisar.',
    action: 'melhorar as instruções de review'
  },
  not_fixable: {
    label: 'Não corrigível (not fixable)',
    meaning: 'É conhecimento de negócio que o modelo não pode ter.',
    action: 'documentar para referência, sem ajuste de prompt'
  }
};

/** Rótulo amigável do lever; devolve o próprio valor quando desconhecido. */
export function leverLabel(lever?: string): string {
  if (!lever) { return ''; }
  return LEVER_INFO[lever]?.label || lever;
}

/** Explicação do lever ("significado — Acionável por: ação"); vazio quando desconhecido. */
export function leverDescription(lever?: string): string {
  const info = lever ? LEVER_INFO[lever] : undefined;
  return info ? `${info.meaning} Ação sugerida: ${info.action}.` : '';
}

/**
 * Filtro de resolução (alimenta o select da fila e do export).
 * Derivado de FINDING_RESOLUTIONS para manter valores/rótulos em sintonia com o
 * card de feedback do review (evita divergência de label, ex.: "Não vou corrigir").
 */
export const RESOLUTION_OPTIONS: PoSelectOption[] = FINDING_RESOLUTIONS.map(
  ({ value, label }) => ({ value, label })
);

/** Filtro de tipo do item. */
export const KIND_OPTIONS: PoSelectOption[] = [
  { value: 'finding', label: 'Achado' },
  { value: 'review', label: 'Review geral' }
];

/** Filtro de tipo do achado (`findingKind`). */
export const FINDING_KIND_OPTIONS: PoSelectOption[] = [
  { value: 'required_action', label: 'Ação obrigatória' },
  { value: 'suggestion', label: 'Sugestão' },
  { value: 'overall', label: 'Geral' }
];

/** Filtro de tipo de review (`resourceType`). */
export const RESOURCE_TYPE_OPTIONS: PoSelectOption[] = [
  { value: 'changeset', label: 'Changeset' },
  { value: 'comparison', label: 'Comparison' },
  { value: 'issue', label: 'Issue' },
  { value: 'standalone', label: 'Standalone' },
  { value: 'pull', label: 'Pull Request' },
  { value: 'commit', label: 'Commit' },
  { value: 'git-comparison', label: 'Git Comparison' }
];

/**
 * Filtro de linguagem (`language`). Valores EXATOS do backend (case-sensitive): a coluna
 * guarda o DisplayName do perfil, e enviar `typescript` não casa com `TypeScript`.
 *
 * A lista espelha `language.ReviewableDisplayNames()` do backend, que tem um teste
 * fixando-a e apontando para cá quando alguém acrescenta uma linguagem. Foi assim que
 * Angular ficou de fora: TypeScript e HTML entraram no registry, a fila passou a receber
 * achados deles, e não havia como recortá-los.
 *
 * Stylesheet não entra: é reconhecida para o arquivo não virar "Undefined", mas não é
 * revisável, e filtrar por ela devolveria sempre vazio.
 */
export const LANGUAGE_OPTIONS: PoSelectOption[] = [
  { value: 'AdvPL', label: 'AdvPL' },
  { value: 'TLPP', label: 'TLPP' },
  { value: 'SQL', label: 'SQL' },
  { value: 'Python', label: 'Python' },
  { value: 'TypeScript', label: 'TypeScript' },
  { value: 'JavaScript', label: 'JavaScript' },
  { value: 'HTML', label: 'HTML' },
  { value: 'Java', label: 'Java' },
  { value: 'C#', label: 'C#' },
  { value: 'Go', label: 'Go' },
  { value: 'Shell', label: 'Shell' },
  { value: 'Docker', label: 'Docker' },
  { value: 'Build/CI', label: 'Build/CI' }
];

/** Filtro de categoria (todas as trilhas) — usado nas abas curados/descartados. */
export const ALL_CATEGORY_OPTIONS: PoSelectOption[] = [...FINDING_CATEGORIES, ...REVIEW_CATEGORIES];

/**
 * Filtro de alavanca (`lever`) — usado nas abas curados/descartados.
 * Derivado de LEVER_INFO (fonte única) para que novos levers apareçam no filtro
 * sem precisar manter uma lista paralela.
 */
export const LEVER_OPTIONS: PoSelectOption[] = Object.keys(LEVER_INFO).map(value => ({
  value,
  label: LEVER_INFO[value].label
}));
